// LEGACY HISTORY — batch 5's rebuild of Migration (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// A CLOSED-HISTORY WINDOW, NEVER DAILY WORK (Astra, legacy): the title and its permanent "Closed history" line, search
// first, the state filter behind Filters, ONE register — a table from lg, a card per record on the phone — whose rows
// say "Closed history" and open the record's detail (?row= in the URL, so Back returns to the list as it was). The list
// opens on "to enter" again, as the old screen did, with Refresh on the laptop (restored 1 Nov 2026). The rare Mark controls (the deal it was booked as, skip, block) are in the
// detail behind "Mark…".
//
// THE READ AND THE WRITE ARE TODAY'S (screens/Migration.tsx): ['migration', status] with staleTime 0 (the list changes
// only because he just changed it), api.migrationMark with the same body and the same invalidation; the matching deals
// are tapped to fill the field, never a booking link (no historical record posts into the current books); a refusal is Refused,
// with its headline. The snapshot's age ("Legacy read 3h ago") stays — it is the difference between "nothing here" and
// "nobody has looked". A list the server cut at its limit says how many it shows of how many.
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api, type MigrationResponse, type MigrationRow, type MigrationStatus } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { fmtAmount, fmtDate, fmtDay, fmtRelative } from '../../lib/format';
import { partyName, shortReference } from '../../lib/naming';
import { Amount } from '../../components/Ledger';
import { CopyRef } from '../../components/CopyRef';
import { LAST_READ_NONE, ReadFailed } from '../../components/OnboardingParts';
import {
  DetailSheet, EmptyState, Fact, Notice, PageHead, Panel, Register, SearchBar, SkeletonRows,
  type RegisterColumn, ReadNotice } from '../../components/v2/kit';
import { DriftNote, Instruction, MarkForm, StateWord, kindWord, type MarkVars } from '../../components/v2/legacy/parts';

type Filter = MigrationStatus | 'all';
const FILTERS: ReadonlyArray<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Not entered' },
  { key: 'entered', label: 'Entered' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'blocked', label: 'Blocked' },
];
const isFilter = (v: string | null): v is Filter => FILTERS.some((f) => f.key === v);

const titleOf = (r: MigrationRow) => [kindWord(r), r.party ? partyName({ display_name: r.party }) : null].filter(Boolean).join(' · ') || 'Legacy record';

/** Does a search find this record? Its party and counterparty, note, kind, references, and figures — typed with or
 *  without the thousands commas. */
function matches(r: MigrationRow, needle: string): boolean {
  const hay = [titleOf(r), r.counterparty ?? '', r.description ?? '', r.legacy_id, r.entered_deal_id ?? '',
    r.entered_deal_id ? shortReference(r.entered_deal_id) : '', r.quantity ?? '', r.total ?? '',
    r.quantity && r.asset ? fmtAmount(r.quantity, r.asset) : '', r.total && r.quote_ccy ? fmtAmount(r.total, r.quote_ccy) : '',
  ].join(' ').toLowerCase();
  const bare = needle.replace(/,/g, '');
  return hay.includes(needle) || (bare !== '' && hay.replace(/,/g, '').includes(bare));
}

const COLUMNS: ReadonlyArray<RegisterColumn<MigrationRow>> = [
  // The day in its own column first, as every register's When (his "one fact per place", 1 Nov 2026: it was glued to "Closed
  // history" under the name; the page's head already says the whole list is closed history).
  { key: 'when', head: 'Day', width: 'w-[7rem]', cell: (r) => <span className="whitespace-nowrap font-mono tabular">{fmtDay(r.occurred_at)}</span> },
  { key: 'record', head: 'Legacy record', cell: (r) => <span className="block min-w-0 break-words">{titleOf(r)}</span> },
  { key: 'amount', head: 'Amount', align: 'right', width: 'w-[24%]', cell: (r) => r.quantity && r.asset ? <Amount value={r.quantity} asset={r.asset} /> : <span className="text-tg-hint">—</span> },
  { key: 'ref', head: 'Live reference', align: 'right', width: 'w-[18%]', cell: (r) => r.entered_deal_id ? <span className="font-mono">{shortReference(r.entered_deal_id)}</span> : <span className="text-tg-hint">—</span> },
  { key: 'state', head: 'State', align: 'right', width: 'w-[16%]', cell: (r) => <span className="inline-flex items-center gap-3"><StateWord r={r} /><ChevronRight className="h-4 w-4 text-tg-hint" aria-hidden /></span> },
];

export function LegacyV2() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  // OPENS ON WHAT IS STILL TO ENTER, as the old screen did (restored 1 Nov 2026); "All" is its own choice in the URL.
  const tab: Filter = isFilter(params.get('status')) ? params.get('status') as Filter : 'pending';
  const [search, setSearch] = useState('');
  const [marking, setMarking] = useState(false);
  const needle = search.trim().toLowerCase();

  const q = useQuery<MigrationResponse>({
    queryKey: ['migration', tab],
    queryFn: () => api.migrationList(tab),
    // This list changes only because the operator just changed it, so a cached copy is never what they want to see.
    staleTime: 0,
  });

  const mark = useMutation({
    mutationFn: (v: MarkVars) => api.migrationMark(v.legacyId, { status: v.status, deal_id: v.deal_id, reason: v.reason }),
    onSuccess: () => {
      setMarking(false);
      qc.invalidateQueries({ queryKey: ['migration'] });
    },
  });

  const rs = readState(q);
  // Oldest first, as the foot says — by when it happened, then by its legacy id (the route's order was not that; Astra, 1 Nov 2026).
  const rows = [...(q.data?.rows ?? [])].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at) || String(a.legacy_id).localeCompare(String(b.legacy_id)));
  const shown = useMemo(() => (needle ? rows.filter((r) => matches(r, needle)) : rows), [rows, needle]);

  const setParam = useCallback((k: string, v: string | null, replace = false) => setParams((x) => {
    const n = new URLSearchParams(x);
    if (v == null) n.delete(k); else n.set(k, v);
    return n;
  }, { replace }), [setParams]);
  const openId = params.get('row');
  const selected = rows.find((r) => r.legacy_id === openId) ?? null;
  const open = (r: MigrationRow) => { setMarking(false); mark.reset(); setParam('row', r.legacy_id); };
  const close = useCallback(() => { setMarking(false); setParam('row', null, true); }, [setParam]);

  // How many records this filter holds, from the server's own count — so a list cut at its limit says so.
  const count = q.data ? (tab === 'all' ? q.data.progress.total : q.data.progress[tab]) : null;
  const age = q.data?.worklist_refreshed_at ? `Legacy read ${fmtRelative(q.data.worklist_refreshed_at)}` : 'Legacy read time unknown';

  const head = (
    <PageHead title="Legacy history" sub="Closed history — nothing here posts to the live books."
      actions={<span className="hidden lg:inline-flex"><button type="button" className="btn-secondary px-3 text-body disabled:opacity-50" disabled={q.isFetching} onClick={() => { void q.refetch(); }}>{q.isFetching ? 'Reading…' : 'Refresh'}</button></span>} />
  );

  if (rs === 'waiting' && !q.data) {
    return <>{head}<Panel flush><SkeletonRows what="legacy history" rows={4} /></Panel></>;
  }
  if (rs === 'failed' && !q.data) {
    return <>{head}<ReadNotice what="legacy history" error={q.error} onRetry={() => { void q.refetch(); }} /></>;
  }

  const filters = (
    <div className="flex flex-wrap gap-2" role="group" aria-label="State">
      {FILTERS.map((f) => (
        <button key={f.key} type="button" aria-pressed={tab === f.key}
          onClick={() => { setParam('status', f.key === 'pending' ? null : f.key); }}
          className={`chip ${tab === f.key ? 'chip-on' : ''}`}>
          {f.label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      {head}
      {/* A failed refresh keeps the rows it had and says so, once; nothing below is counted from them. */}
      {rs === 'failed' && (
        <Notice tone="warn">
          <ReadFailed what="legacy history" error={q.error} says={rows.length > 0 ? 'showing the last good read' : LAST_READ_NONE} retry={() => { void q.refetch(); }} />
        </Notice>
      )}

      <SearchBar value={search} onChange={setSearch} placeholder="Search" filters={filters} active={tab === 'pending' ? 0 : 1} />

      <Panel flush>
        {shown.length > 0 && (
          <Register label="Legacy records" columns={COLUMNS} rows={shown} rowKey={(r) => r.legacy_id} onOpen={open}
            card={(r) => ({
              // The amount on its own line under the name, never squeezing it beside (the audit, 1 Nov 2026: four lines of name).
              title: <>{titleOf(r)}{r.quantity && r.asset && <span className="mt-1 block text-body font-normal"><Amount value={r.quantity} asset={r.asset} /></span>}</>,
              meta: [fmtDay(r.occurred_at), r.entered_deal_id ? shortReference(r.entered_deal_id) : null].filter(Boolean).join(' · '),
              state: <StateWord r={r} />,
            })} />
        )}
        {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ. */}
        {shown.length === 0 && rs === 'known' && (
          <EmptyState className="px-4 lg:py-16" action={needle ? <button type="button" className="btn-link" onClick={() => setSearch('')}>Clear search</button> : undefined}>
            {needle ? 'Nothing matches this search.' : 'Nothing here.'}
          </EmptyState>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
          <span className="min-w-0">
            {[rs === 'known' && !needle && (count != null && count > rows.length ? `${rows.length} of ${count} records` : `${rows.length} ${rows.length === 1 ? 'record' : 'records'}`),
              'Oldest first'].filter(Boolean).join(' · ')}
          </span>
          <span>{age}</span>
        </div>
      </Panel>

      {selected && (
        <DetailSheet title={titleOf(selected)} onClose={close}>
          <div>
            <Fact label="Record"><Instruction r={selected} /></Fact>
            {selected.party && <Fact label="Party">{partyName({ display_name: selected.party })}</Fact>}
            {selected.counterparty && <Fact label="Counterparty">{partyName({ display_name: selected.counterparty })}</Fact>}
            <Fact label="Typed">{fmtDate(selected.occurred_at)}</Fact>
            {selected.description && <Fact label="Note">&ldquo;{selected.description}&rdquo;</Fact>}
            <Fact label="State"><StateWord r={selected} /></Fact>
            {selected.entered_deal_id && <Fact label="Live reference"><CopyRef value={selected.entered_deal_id} label={shortReference(selected.entered_deal_id)} /></Fact>}
            {selected.skip_reason && <Fact label="Reason">{selected.skip_reason}</Fact>}
            <Fact label="Legacy id"><CopyRef value={selected.legacy_id} /></Fact>
          </div>
          {selected.drift_after && <div className="mt-4"><DriftNote r={selected} /></div>}
          {selected.status === 'pending' && (
            <div className="mt-6">
              {marking
                ? <MarkForm r={selected} pending={mark.isPending} error={mark.isError ? mark.error : null} onMark={(v) => mark.mutate(v)} onCancel={() => setMarking(false)} />
                : <button type="button" className="btn-secondary px-4 text-body" onClick={() => { mark.reset(); setMarking(true); }}>Mark…</button>}
            </div>
          )}
        </DetailSheet>
      )}
    </>
  );
}
